"use client";

import { formatLocalDate } from "@/utils/formatLocalDate";
import { formatUnitPrice } from "@/utils/utils";
import { ChartOptions, ColorType, createChart, DeepPartial, IChartApi, LineStyle } from "lightweight-charts";
import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState, useSyncExternalStore } from "react";
import { ChartContext } from "./context";

function resolveCssColorVar( name: string, fallback: string ): string {
    if ( typeof document === "undefined" ) return fallback;
    const raw = getComputedStyle( document.documentElement ).getPropertyValue( name ).trim();
    return raw || fallback;
}

function subscribeDomTheme( onStoreChange: () => void ) {
    const mo = new MutationObserver( onStoreChange );
    mo.observe( document.documentElement, { attributes: true, attributeFilter: [ "data-theme", "class" ] } );
    return () => mo.disconnect();
}

function getDomThemeSnapshot() {
    return `${document.documentElement.dataset.theme ?? ""}|${document.documentElement.className}`;
}

function getServerDomThemeSnapshot() {
    return "light|";
}

export interface LightweightChartProps {
    children?: React.ReactNode;
    options?: DeepPartial<ChartOptions>;
    className?: string;
    height?: number | string;
    priceFormatter?: ( price: number ) => string;
}

/**
 * 图表容器组件 - 负责初始化图表并管理其生命周期
 */
export const LightweightChart = forwardRef<IChartApi | null, LightweightChartProps>(
    ( { children, options, className: _className, height: _height = "300px", priceFormatter }, ref ) => {
        const chartContainerRef = useRef<HTMLDivElement>( null );
        const [ chartApi, setChartApi ] = useState<IChartApi | null>( null );
        const themeTag = useSyncExternalStore( subscribeDomTheme, getDomThemeSnapshot, getServerDomThemeSnapshot );
        const axisTextColor = resolveCssColorVar( "--ui-content-t3", "#B2B2B2" );
        const priceAxisTextColor = resolveCssColorVar( "--ui-content-t4", "#D1D5DB" );
        const timeScaleBorderColor = resolveCssColorVar( "--ui-stroke-default", "#E6E8EA" );
        const crosshairLabelBg = resolveCssColorVar( "--ui-surface-muted", "#E6E8EA" );
        const horzGridLineColor = resolveCssColorVar( "--ui-stroke-muted", "rgba(0,0,0,0.2)" );
        const defaultPriceFormatter = ( price: number ) => formatUnitPrice( price, { precision: 0, useScale: false } );
        const resolvedPriceFormatter = priceFormatter ?? options?.localization?.priceFormatter ?? defaultPriceFormatter;

        // 默认主题样式（LW 走 canvas，须用解析后的色值；随主题用 themeTag 触发 applyOptions）
        const defaultOptions: DeepPartial<ChartOptions> = {
            layout: {
                background: { type: ColorType.Solid, color: "transparent" },
                textColor: axisTextColor,
                fontSize: 12,
                ["attributionLogo" as any]: false,
                fontFamily: "'OpenSauceOne', BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif",
            },
            grid: {
                vertLines: { visible: false },
                horzLines: { color: horzGridLineColor, style: LineStyle.SparseDotted, visible: true },
            },
            rightPriceScale: {
                visible: true,
                alignLabels: false,
                borderVisible: false,
                textColor: priceAxisTextColor,
                scaleMargins: { top: 0, bottom: 0.01 }, // 调整留白，让数据稍微居中一点
            },
            timeScale: {
                rightOffsetPixels: 20,
                minBarSpacing: 1,
                borderVisible: true,
                borderColor: timeScaleBorderColor,
                timeVisible: true,
                // secondsVisible: true,
                allowBoldLabels: false,
                uniformDistribution: false,
                // 自定义时间轴格式：HH:mm
                tickMarkFormatter: ( time: number ) => {
                    return formatLocalDate( time, "HH:mm:ss" );
                },
            },
            // 自定义价格轴（Y轴）文本格式
            localization: {
                dateFormat: "HH:mm:ss",
                priceFormatter: resolvedPriceFormatter,
                // 自定义十字光标处的时间格式
                timeFormatter: ( time: number ) => {
                    return formatLocalDate( time, "HH:mm:ss" );
                },
            },
            handleScroll: { mouseWheel: false, pressedMouseMove: false },
            handleScale: { axisPressedMouseMove: true, mouseWheel: true, pinch: true },
            crosshair: {
                // Vertical crosshair line (showing Date in Label)
                vertLine: {
                    width: 1,
                    color: "#C3BCDB44",
                    style: LineStyle.Solid,
                    labelBackgroundColor: crosshairLabelBg,
                    visible: false,
                },

                // Horizontal crosshair line (showing Price in Label)
                horzLine: {
                    width: 1,
                    color: "#C3BCDB44",
                    style: LineStyle.Solid,
                    labelBackgroundColor: crosshairLabelBg,
                    visible: false,
                },
            },
        };

        useEffect( () => {
            if ( !chartContainerRef.current ) return;

            // 初始化图表
            const chart = createChart( chartContainerRef.current, {
                ...defaultOptions,
                ...options,
                localization: {
                    ...defaultOptions.localization,
                    ...options?.localization,
                    priceFormatter: resolvedPriceFormatter,
                },
                width: chartContainerRef.current.clientWidth,
                // 如果传入的是字符串（如 300px），由于 LW 不直接支持字符串尺寸，我们需要计算
                height: chartContainerRef.current.clientHeight,
            } );

            setChartApi( chart );

            // 自动响应容器大小变化
            const handleResize = () => {
                if ( chartContainerRef.current ) {
                    chart.applyOptions( {
                        width: chartContainerRef.current.clientWidth,
                        height: chartContainerRef.current.clientHeight,
                    } );
                }
            };

            window.addEventListener( "resize", handleResize );

            return () => {
                window.removeEventListener( "resize", handleResize );
                chart.remove();
            };
            // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [] );

        useEffect( () => {
            if ( !chartApi ) return;
            chartApi.applyOptions( {
                layout: { textColor: resolveCssColorVar( "--ui-content-t3", "#B2B2B2" ) },
                rightPriceScale: { textColor: resolveCssColorVar( "--ui-content-t4", "#D1D5DB" ) },
                timeScale: { borderColor: resolveCssColorVar( "--ui-stroke-default", "#E6E8EA" ) },
                grid: { horzLines: { color: resolveCssColorVar( "--ui-stroke-muted", "rgba(0,0,0,0.2)" ) } },
                crosshair: {
                    vertLine: { labelBackgroundColor: resolveCssColorVar( "--ui-surface-muted", "#E6E8EA" ) },
                    horzLine: { labelBackgroundColor: resolveCssColorVar( "--ui-surface-muted", "#E6E8EA" ) },
                },
            } );
        }, [ chartApi, themeTag ] );

        // 合并外部传入的配置
        useEffect( () => {
            if ( chartApi && options ) {
                chartApi.applyOptions( options );
            }
        }, [ chartApi, options ] );

        // 暴露实例给外部 ref
        useImperativeHandle( ref, () => chartApi!, [ chartApi ] );

        return (
            <div className={`relative w-full h-full`}>
                <div ref={chartContainerRef} className="w-full h-full" />
                {chartApi && (
                    <ChartContext.Provider value={{ chart: chartApi, container: chartContainerRef.current }}>
                        {children}
                    </ChartContext.Provider>
                )}
            </div>
        );
    },
);

LightweightChart.displayName = "LightweightChart";
